import React, { useEffect, useState, useMemo } from 'react';

import {
  Box,
  Typography,
  List,
  CircularProgress,
  Alert,
  Backdrop,
} from '@mui/material';
import ExpandableTypography from 'src/common/components/ExpandableTypography';
import TextViewableInPopup from 'src/common/components/TextViewableInPopup';
import {
  ClinicResponse,
  GenerativeContentType,
} from 'src/common/types/ClinicResponse';

import { ReactComponent as SearchIconWhite } from '../../../common/assets/icons/search_icon_transparent.svg';
import { ICD10Code, SelectableCode } from '../../../common/types/Codes';
import { SaveSheet } from '../../../common/types/SaveSheet';
import AddCodeContainer from '../AddCodeContainer/AddCodeContainer';
import SearchResultCell from '../SearchResultCell/SearchResultCell';

import SelectNumberOfResultsButton from './components/SelectNumberOfResultsButton';

import './SearchResultsContainer.scss';
import AddManualCodeButton from './components/AddManualCodeButton';
import SmallEditButton from './components/SmallEditButton';
import UseAllCodesButton from './components/UseAllCodesButton';
import FilterCodesButton from './components/FilterCodesButton';
import SearchedTextArea from './components/SearchedTextArea';
import SelectClinicNoteStatus from './components/SelectClinicNoteStatus';
import EditGenerativeContentPopup from './components/EditGenerativeContentPopup';

type Props = {
  searchText?: string;
  searchType: 'text' | 'scan';
  searchResults: SelectableCode[];
  currentNote?: SaveSheet | null;
  selectCode: (code: SelectableCode) => void;
  selectedAll: (select: boolean) => void;
  selectedFilterIcon: () => void;
  selectMultiplier: (code: SelectableCode, multiplier: number) => void;
  selectModifiers: (code: SelectableCode, modifiers: string[]) => void;
  flagCode: (code: SelectableCode) => void;
  limit: 10 | 15 | 25 | 10000;
  changeLimit: (limit: 10 | 15 | 25 | 10000) => void;
  refreshTextSearch?: (text: string) => void;
  refetchCodes: () => Promise<void>;
  filtersLength: number;
  isLoading: boolean;
  hasSelectedAll: boolean;
  clinicResponse?: ClinicResponse | null;
  updateClinicNoteStatus: (status: string) => void;
  clearSelectedCodes: () => void;
  updateNote: (
    note: SaveSheet,
    showConfirmationMessage: boolean
  ) => Promise<void>;
  allowNoteTextEditing: boolean;
  isSmartLaunch?: boolean;
  feeScheduleCodes?: string[];
  addManualCode: (code: SelectableCode) => void;
  selectedCodes?: SelectableCode[];
  hasMultiplePrimaryCodes?: boolean;
  updateICD10?: (code: SelectableCode, icd10: ICD10Code[]) => void;
  suggestedICD10?: ICD10Code[];
};

const SearchResultsContainer: React.FC<Props> = ({
  searchResults,
  searchText,
  searchType,
  currentNote,
  selectCode,
  selectedAll,
  selectedFilterIcon,
  selectMultiplier,
  selectModifiers,
  flagCode,
  limit,
  changeLimit,
  refreshTextSearch,
  refetchCodes,
  filtersLength,
  isLoading,
  hasSelectedAll,
  clinicResponse,
  updateClinicNoteStatus,
  clearSelectedCodes,
  updateNote,
  allowNoteTextEditing,
  isSmartLaunch = false,
  feeScheduleCodes = [],
  addManualCode,
  selectedCodes = [],
  hasMultiplePrimaryCodes = false,
  updateICD10,
  suggestedICD10 = [],
}) => {
  // state
  const [cptCodes, setCptCodes] = useState<SelectableCode[]>([]);
  const [hcpcsCodes, setHcpcsCodes] = useState<SelectableCode[]>([]);

  const [addCodePopup, setAddCodePopup] = useState<'CPT' | 'HCPCS' | undefined>(
    undefined
  );

  const [forceChangeClinicNoteStatus, setForceChangeClinicNoteStatus] =
    useState(false);

  const [editGenerativeContent, setEditGenerativeContent] = useState<{
    type: GenerativeContentType;
    value: string;
  }>({ type: null, value: '' });

  useEffect(() => {
    const newCptCodes = searchResults.filter(
      (result) => !isNaN(Number(result.code))
    );
    const newHcpcsCodes = searchResults.filter((result) =>
      isNaN(Number(result.code))
    );

    setCptCodes(newCptCodes);
    setHcpcsCodes(newHcpcsCodes);
  }, [searchResults]);

  // Regenerate Clinic Note results when pressing Shift + R
  useEffect(() => {
    if (!currentNote?.clinicNote) return;

    const onKeyDown = async (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === '1') {
        event.preventDefault();
        clearSelectedCodes();
        await refetchCodes();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [clearSelectedCodes, refetchCodes, currentNote?.clinicNote]);

  // Utils
  const allowManualCodes = useMemo(() => {
    if (!currentNote?.clinicNote) return true;
    if (currentNote?.clinicNote && searchResults.length) return true;

    return false;
  }, [currentNote, searchResults.length]);

  return (
    <Box
      className='content-container search-results-container'
      sx={{
        overflow: 'hidden',
        textAlign: 'left',
        backgroundColor: currentNote?.clinicNote ? 'transparent' : '#FFFFFF',
        borderWidth: currentNote?.clinicNote ? '0' : '1px',
        marginTop: '24px !important',
      }}
    >
      {/* Header with the searched text */}
      {!isSmartLaunch && (
        <Box
          className='search-results-header-container'
          sx={{
            display: 'flex',
            flexGrow: 1,
            flex: 1,
            flexDirection: 'row',
            backgroundColor: currentNote?.clinicNote
              ? 'transparent'
              : '#81B5CA',
            padding: currentNote?.clinicNote ? '0px' : '30px',
            marginBottom: currentNote?.clinicNote ? '24px' : '0',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            {!currentNote?.clinicNote &&
              (isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    color: currentNote?.clinicNote ? 'black' : 'white',
                  }}
                />
              ) : (
                <SearchIconWhite
                  style={{
                    flexShrink: 0,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
              ))}
          </Box>
          <SearchedTextArea
            searchType={searchType}
            searchText={searchText}
            refreshResults={refreshTextSearch}
            filename={currentNote?.scanDocumentUrl ?? ''}
            savedSheet={currentNote}
            allowNoteTextEditing={allowNoteTextEditing}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: '24px',
          flexDirection: {
            xs: 'column',
            xl: 'row',
          },
        }}
      >
        <Box
          sx={{
            padding: currentNote?.clinicNote ? '0px' : '0 20px',
            flex: 1,
            maxWidth: {
              xs: '100%',
              xl: currentNote?.clinicNote ? '50%' : '100%',
            },
            backgroundColor: currentNote?.clinicNote
              ? 'transparent'
              : '#D8F4F8',
          }}
        >
          <Box
            className={
              currentNote?.clinicNote ? 'clinic-note-codes-container' : ''
            }
          >
            {/* CPT Codes Heading  */}
            <Box
              sx={{
                marginTop: '16px',
                marginBottom: '16px',
                gap: '16px',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                  }}
                />
              ) : (
                <Typography
                  color={currentNote?.clinicNote ? 'white' : 'darkBlue'}
                  fontSize={20}
                  fontWeight={700}
                >
                  {cptCodes?.length ?? 0}{' '}
                  {cptCodes?.length === 1
                    ? 'CPT code found'
                    : 'CPT codes found'}
                </Typography>
              )}

              <Box className='row' sx={{ flexWrap: 'wrap', gap: '8px' }}>
                {searchType === 'scan' && (
                  <>
                    {allowManualCodes && (
                      <AddManualCodeButton
                        isLoading={isLoading}
                        type='CPT'
                        openAddCodePopup={setAddCodePopup}
                        dark={!currentNote?.clinicNote}
                      />
                    )}
                    {!currentNote?.clinicNote && (
                      <SelectNumberOfResultsButton
                        value={limit}
                        onChange={changeLimit}
                      />
                    )}
                    {!currentNote?.clinicNote && (
                      <UseAllCodesButton
                        isLoading={isLoading}
                        selectedAll={selectedAll}
                        hasSelectedAll={hasSelectedAll}
                      />
                    )}
                  </>
                )}
                {!currentNote?.clinicNote && (
                  <FilterCodesButton
                    filtersLength={filtersLength}
                    selectedFilterIcon={selectedFilterIcon}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Box>

            {!isLoading && hasMultiplePrimaryCodes && (
              <Alert
                severity='error'
                color='error'
                variant='standard'
                sx={{
                  marginTop: '8px',
                  marginBottom: '8px',
                  backgroundColor: '#F9D7D7',
                  borderRadius: '16px',
                  padding: '8px 16px',
                }}
              >
                <Typography color='error' fontWeight={700} fontSize='16px'>
                  GaleAI has recommended a different code than what is currently
                  in the Chart. Please review suggested code and reasoning, and
                  select the most appropriate code.
                </Typography>
              </Alert>
            )}

            {/* List of CPT codes for Clinic Notes (including Smart Launches) */}
            {currentNote?.clinicNote && cptCodes.length > 0 && !isLoading && (
              <Box>
                <List style={{ paddingBottom: '20px', marginTop: '8px' }}>
                  {cptCodes.map((result) => (
                    <SearchResultCell
                      key={result.code}
                      code={result as SelectableCode}
                      sheetId={currentNote?.sheetId ?? 0}
                      selectCode={selectCode}
                      selectMultiplier={selectMultiplier}
                      selectModifiers={selectModifiers}
                      flagCode={flagCode}
                      searchType={searchType}
                      isClinicNote={true}
                      isSmartLaunch={isSmartLaunch}
                      existsInFeeSchedule={feeScheduleCodes.includes(
                        result.code
                      )}
                      hasMultiplePrimaryCodes={hasMultiplePrimaryCodes}
                      updateICD10={updateICD10}
                      suggestedICD10={suggestedICD10}
                    />
                  ))}
                </List>
              </Box>
            )}

            {/* List of CPT codes for OP Notes */}
            {!currentNote?.clinicNote && !isLoading && (
              <List style={{ paddingBottom: '20px' }}>
                <Box>
                  {cptCodes.map((result) => (
                    <SearchResultCell
                      key={result.code}
                      code={result as SelectableCode}
                      sheetId={currentNote?.sheetId ?? 0}
                      selectCode={selectCode}
                      selectMultiplier={selectMultiplier}
                      selectModifiers={selectModifiers}
                      flagCode={flagCode}
                      searchType={searchType}
                      isClinicNote={false}
                    />
                  ))}
                </Box>
              </List>
            )}

            {/* List of HCPCS codes for Clinic Notes (including Smart Launches) */}
            {currentNote?.clinicNote && !isLoading && (
              <Box
                sx={{
                  marginTop: '56px',
                  marginBottom: '16px',
                  gap: '16px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                }}
              >
                {!isLoading && (
                  <Typography
                    color={currentNote?.clinicNote ? 'white' : 'darkBlue'}
                    fontSize={20}
                    fontWeight={700}
                  >
                    {hcpcsCodes?.length ?? 0}{' '}
                    {hcpcsCodes?.length === 1
                      ? 'HCPCS code found'
                      : 'HCPCS codes found'}
                  </Typography>
                )}
                {allowManualCodes && (
                  <AddManualCodeButton
                    isLoading={isLoading}
                    type='HCPCS'
                    openAddCodePopup={setAddCodePopup}
                    dark={!currentNote?.clinicNote}
                  />
                )}
              </Box>
            )}

            {currentNote?.clinicNote && !isLoading && hcpcsCodes.length > 0 && (
              <Box>
                <List style={{ paddingBottom: '20px', marginTop: '8px' }}>
                  {hcpcsCodes.map((result) => (
                    <SearchResultCell
                      key={result.code}
                      code={result as SelectableCode}
                      sheetId={currentNote?.sheetId ?? 0}
                      selectCode={selectCode}
                      selectMultiplier={selectMultiplier}
                      selectModifiers={selectModifiers}
                      flagCode={flagCode}
                      searchType={searchType}
                      isClinicNote={true}
                      isSmartLaunch={isSmartLaunch}
                      existsInFeeSchedule={feeScheduleCodes.includes(
                        result.code
                      )}
                      updateICD10={updateICD10}
                      suggestedICD10={suggestedICD10}
                    />
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Box>

        {currentNote?.clinicNote && (
          <Box
            sx={{
              flex: 1,
              maxWidth: {
                xs: '100%',
                xl: '50%',
              },
            }}
          >
            <Box
              className='clinic-note-codes-container'
              sx={{ paddingBottom: '32px' }}
            >
              <Box className='generative-title-container'>
                <Typography fontSize={18} fontWeight={600} color='white'>
                  Clinic Note Status
                </Typography>

                {clinicResponse?.status_response !== 'unknown' &&
                  !forceChangeClinicNoteStatus &&
                  !isLoading && (
                    <SmallEditButton
                      text='Wrong status?'
                      isLoading={isLoading}
                      onClick={() => setForceChangeClinicNoteStatus(true)}
                    />
                  )}
              </Box>

              {clinicResponse?.status_response === 'unknown' &&
                !isLoading &&
                !forceChangeClinicNoteStatus && (
                  <Alert
                    severity='error'
                    color='error'
                    variant='standard'
                    sx={{
                      marginTop: '8px',
                      marginBottom: '8px',
                      borderRadius: '16px',
                    }}
                  >
                    Unsure of visit type, please select type of visit.
                  </Alert>
                )}
              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                />
              ) : clinicResponse?.status_response === 'unknown' ||
                forceChangeClinicNoteStatus ? (
                <SelectClinicNoteStatus
                  isLoading={isLoading}
                  onSave={(status) => {
                    updateClinicNoteStatus(status);
                    if (forceChangeClinicNoteStatus)
                      setForceChangeClinicNoteStatus(false);
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <Box sx={{ width: '120px' }}>
                    <Typography
                      fontSize={16}
                      fontWeight={500}
                      sx={{ color: '#ffffff' }}
                    >
                      {clinicResponse?.status_response ?? 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box className='generative-title-container'>
                <Typography fontSize={18} fontWeight={600} color='white'>
                  Final Code
                </Typography>
                {clinicResponse?.final_code && (
                  <SmallEditButton
                    isLoading={isLoading}
                    onClick={() =>
                      setEditGenerativeContent({
                        type: 'final_code',
                        value: clinicResponse.final_code,
                      })
                    }
                  />
                )}
              </Box>

              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                />
              ) : (
                <Typography
                  fontSize={16}
                  fontWeight={500}
                  sx={{ marginBottom: '24px', color: '#ffffff' }}
                >
                  {clinicResponse?.final_code ?? 'N/A'}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gridTemplateRows: '1fr',
                  gap: '28px',
                }}
              >
                <Box>
                  <Box className='generative-title-container'>
                    <Typography fontSize={18} fontWeight={600} color='white'>
                      ICD-10 Codes
                    </Typography>
                    {clinicResponse?.icd_response && (
                      <SmallEditButton
                        isLoading={isLoading}
                        onClick={() =>
                          setEditGenerativeContent({
                            type: 'icd_response',
                            value: clinicResponse.icd_response,
                          })
                        }
                      />
                    )}
                  </Box>

                  {isLoading ? (
                    <CircularProgress
                      size='20px'
                      sx={{
                        color: 'primary',
                        marginTop: '16px',
                        marginBottom: '16px',
                      }}
                    />
                  ) : (
                    clinicResponse?.icd_response && (
                      <TextViewableInPopup title='ICD-10 Codes'>
                        {clinicResponse.icd_response}
                      </TextViewableInPopup>
                    )
                  )}
                </Box>

                <Box>
                  <Box className='generative-title-container'>
                    <Typography fontSize={18} fontWeight={600} color='white'>
                      CPT Codes
                    </Typography>
                    {clinicResponse?.cpt_response && (
                      <SmallEditButton
                        isLoading={isLoading}
                        onClick={() =>
                          setEditGenerativeContent({
                            type: 'cpt_response',
                            value: clinicResponse.cpt_response,
                          })
                        }
                      />
                    )}
                  </Box>

                  {isLoading ? (
                    <CircularProgress
                      size='20px'
                      sx={{
                        color: 'primary',
                        marginTop: '16px',
                        marginBottom: '16px',
                      }}
                    />
                  ) : (
                    clinicResponse?.cpt_response && (
                      <TextViewableInPopup title='CPT Codes'>
                        {clinicResponse.cpt_response}
                      </TextViewableInPopup>
                    )
                  )}
                </Box>

                <Box>
                  <Box className='generative-title-container'>
                    <Typography fontSize={18} fontWeight={600} color='white'>
                      HCPCS Codes
                    </Typography>
                    {clinicResponse?.hcpc_response && (
                      <SmallEditButton
                        isLoading={isLoading}
                        onClick={() =>
                          setEditGenerativeContent({
                            type: 'hcpc_response',
                            value: clinicResponse.hcpc_response,
                          })
                        }
                      />
                    )}
                  </Box>

                  {isLoading ? (
                    <CircularProgress
                      size='20px'
                      sx={{
                        color: 'primary',
                        marginTop: '16px',
                        marginBottom: '16px',
                      }}
                    />
                  ) : (
                    clinicResponse?.hcpc_response && (
                      <TextViewableInPopup title='HCPCS Codes'>
                        {clinicResponse.hcpc_response}
                      </TextViewableInPopup>
                    )
                  )}
                </Box>
              </Box>

              <Box className='generative-title-container'>
                <Typography fontSize={18} fontWeight={600} color='white'>
                  Problem Complexity Assessment
                </Typography>
                {clinicResponse?.problem_response && (
                  <SmallEditButton
                    isLoading={isLoading}
                    onClick={() =>
                      setEditGenerativeContent({
                        type: 'problem_response',
                        value: clinicResponse.problem_response,
                      })
                    }
                  />
                )}
              </Box>

              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                />
              ) : (
                clinicResponse?.problem_response && (
                  <ExpandableTypography
                    lineCount={6}
                    fontSize={16}
                    color='white'
                  >
                    {clinicResponse.problem_response}
                  </ExpandableTypography>
                )
              )}

              <Box className='generative-title-container'>
                <Typography fontSize={18} fontWeight={600} color='white'>
                  Data Complexity Assessment
                </Typography>
                {clinicResponse?.data_response && (
                  <SmallEditButton
                    isLoading={isLoading}
                    onClick={() =>
                      setEditGenerativeContent({
                        type: 'data_response',
                        value: clinicResponse.data_response,
                      })
                    }
                  />
                )}
              </Box>

              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                />
              ) : (
                clinicResponse?.data_response && (
                  <ExpandableTypography
                    lineCount={6}
                    fontSize={16}
                    color='white'
                  >
                    {clinicResponse.data_response}
                  </ExpandableTypography>
                )
              )}

              <Box className='generative-title-container'>
                <Typography fontSize={18} fontWeight={600} color='white'>
                  Risk Complexity Assessment
                </Typography>
                {clinicResponse?.risk_response && (
                  <SmallEditButton
                    isLoading={isLoading}
                    onClick={() =>
                      setEditGenerativeContent({
                        type: 'risk_response',
                        value: clinicResponse.risk_response,
                      })
                    }
                  />
                )}
              </Box>

              {isLoading ? (
                <CircularProgress
                  size='20px'
                  sx={{
                    color: 'primary',
                    marginTop: '16px',
                    marginBottom: '16px',
                  }}
                />
              ) : (
                clinicResponse?.risk_response && (
                  <ExpandableTypography
                    lineCount={6}
                    fontSize={16}
                    color='white'
                  >
                    {clinicResponse.risk_response}
                  </ExpandableTypography>
                )
              )}
            </Box>
          </Box>
        )}
      </Box>

      <EditGenerativeContentPopup
        type={editGenerativeContent.type}
        value={editGenerativeContent.value}
        currentNote={currentNote}
        onClose={() => setEditGenerativeContent({ type: null, value: '' })}
        updateNoteFully={updateNote}
        clinicResponse={clinicResponse}
      />

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1101,
          justifyContent: 'center',
          alignItems: 'center',
        }}
        open={addCodePopup !== undefined}
      >
        {addCodePopup && (
          <AddCodeContainer
            isClinicNote={!!currentNote?.clinicNote}
            selectedCodes={selectedCodes ?? []}
            addManualCode={addManualCode}
            onClickClose={() => {
              setAddCodePopup(undefined);
            }}
            type={addCodePopup}
          />
        )}
      </Backdrop>
    </Box>
  );
};

export default SearchResultsContainer;
